/**
 * Produce by Month Component for v2v2 SWFL Farmers Market Application
 * List view of produce by month with expandable sections
 */



import { useState, useMemo } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  Leaf, 
  Sprout, 
  Clock,
  MapPin,
  ShoppingBag
} from 'lucide-react';
import { useSeasonal } from '@/hooks/use-seasonal';
import { cn } from '@/lib/utils';
import { Month } from '@/types/seasonal';

interface ProduceByMonthProps {
  className?: string;
  defaultExpanded?: boolean;
  showShoppingTips?: boolean;
  showMarkets?: boolean;
}

const MONTHS: Month[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface MonthSectionProps {
  month: Month;
  availability: {
    peak: string[];
    available: string[];
    limited: string[];
    highlights: string;
    farmersMarketTips: string;
  };
  isCurrentMonth: boolean;
  defaultExpanded: boolean;
  showShoppingTips: boolean;
  showMarkets: boolean;
}

function MonthSection({
  month,
  availability,
  isCurrentMonth,
  defaultExpanded,
  showShoppingTips,
  showMarkets,
}: MonthSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || isCurrentMonth);

  return (
    <div className={cn(
      'border rounded-lg overflow-hidden',
      isCurrentMonth ? 'border-green-400 bg-green-50/30' : 'border-muted'
    )}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{isCurrentMonth && '🌟'}</span>
          <span className="font-semibold text-lg">{month}</span>
          {isCurrentMonth && (
            <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
              Current
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {availability.peak.length} peak
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Highlights */}
          <p className="text-sm text-muted-foreground italic">
            {availability.highlights}
          </p>

          {/* Peak Produce */}
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2 text-green-700">
              <Leaf className="h-4 w-4" /> Peak Season
            </h4>
            <div className="flex flex-wrap gap-2">
              {availability.peak.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 border border-green-300"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Available Produce */}
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2 text-forest">
              <Sprout className="h-4 w-4" /> Also Available
            </h4>
            <div className="flex flex-wrap gap-2">
              {availability.available.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1 rounded-full text-sm bg-sage text-bark border border-forest-light"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Limited Produce */}
          {availability.limited.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2 text-orange-700">
                <Clock className="h-4 w-4" /> Limited
              </h4>
              <div className="flex flex-wrap gap-2">
                {availability.limited.map((item) => (
                  <span
                    key={item}
                    className="px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800 border border-orange-300"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Shopping Tips */}
          {showShoppingTips && availability.farmersMarketTips && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm flex items-start gap-2">
                <ShoppingBag className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{availability.farmersMarketTips}</span>
              </p>
            </div>
          )}

          {/* Markets Link */}
          {showMarkets && isCurrentMonth && (
            <button className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
              <MapPin className="h-4 w-4" />
              Find Markets for {month}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ProduceByMonth({
  className,
  defaultExpanded = false,
  showShoppingTips = true,
  showMarkets = true,
}: ProduceByMonthProps) {
  const { currentMonth, getAvailability, isLoading } = useSeasonal();

  const monthsWithAvailability = useMemo(() => {
    return MONTHS.map((month) => ({
      month,
      availability: getAvailability(month),
      isCurrentMonth: month === currentMonth,
    }));
  }, [getAvailability, currentMonth]);

  if (isLoading) {
    return (
      <div className={cn('animate-pulse space-y-4', className)}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {monthsWithAvailability.map(({ month, availability, isCurrentMonth }) => (
        availability && (
          <MonthSection
            key={month}
            month={month}
            availability={availability}
            isCurrentMonth={isCurrentMonth}
            defaultExpanded={defaultExpanded}
            showShoppingTips={showShoppingTips}
            showMarkets={showMarkets}
          />
        )
      ))}
    </div>
  );
}
